/**
 * Phase N — /refund-policy · Refund Policy (draft, pending legal review).
 *
 * TODO: confirm exact refund terms with counsel and finance.
 */
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import LegalReviewBanner from "@/components/LegalReviewBanner";

const LAST_UPDATED = "February 2026";

function LegalSection({ title, id, testId, children }) {
  return (
    <section id={id} data-testid={testId} className="mt-10">
      <h2 className="font-serif text-2xl text-ivy tracking-tight">{title}</h2>
      <div className="mt-4 text-ink-soft leading-relaxed space-y-3 text-[15px]">{children}</div>
    </section>
  );
}

export default function RefundPolicy() {
  return (
    <Layout>
      <SEO
        title="Refund Policy | Beyond Valencia"
        description="Beyond Valencia refund policy. Free tools stay free. Paid diagnostics, audits, and advising engagements have clear windows and pro-rated refund rules."
        canonical="/refund-policy"
      />
      <LegalReviewBanner />

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-20" data-testid="refund-page">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Legal · Draft</div>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-ivy tracking-tight" data-testid="refund-h1">
          Refund Policy
        </h1>
        <p className="mt-3 text-sm text-stone">Last updated: {LAST_UPDATED}</p>

        <p className="mt-8 text-ink-soft leading-relaxed">
          We want every student who pays us to feel that the exchange was fair. This document explains what is
          refundable, how to request a refund, and what the timelines look like.
        </p>

        <LegalSection title="1. Free tools stay free" id="free" testId="refund-free">
          <p>The Mishap Corrector, requirement lookups, pair pages, guides, and blog content are free and require no payment.</p>
        </LegalSection>

        <LegalSection title="2. One-time paid audits" id="audits" testId="refund-audits">
          <p>
            For one-time paid audits and diagnostics (including the Transfer Audit): you may request a full refund
            within seven (7) days of purchase, provided no live adviser session has been delivered and no bespoke
            written deliverable has been sent to you.
          </p>
          <p>Once a live adviser session has taken place or a bespoke written deliverable has been delivered, the audit fee becomes non-refundable.</p>
        </LegalSection>

        <LegalSection title="3. Multi-session advising engagements" id="engagements" testId="refund-engagements">
          <p>
            For paid multi-session advising engagements (e.g., Florida Transfer or partner-routed Ivy engagements),
            unused sessions are refundable on a pro-rated basis less any hours already delivered. Refund requests
            must be made in writing to{" "}
            {/* TODO: confirm mailbox routing */}
            <a href="mailto:hello@beyondvalencia.com" className="text-ivy hover:text-brass underline" data-testid="refund-contact-email">
              hello@beyondvalencia.com
            </a>{" "}
            within thirty (30) days of the most recent session.
          </p>
        </LegalSection>

        <LegalSection title="4. Partner-routed services" id="partners" testId="refund-partners">
          <p>
            When your inquiry is routed to Ivy League Consulting or another partner and a contract is executed
            directly with that partner, refund terms are governed by that partner&apos;s policy. Beyond Valencia
            will refund any handling fee we collected from you within seven (7) days of the intake if the partner
            engagement did not materialize.
          </p>
        </LegalSection>

        <LegalSection title="5. Processing time" id="processing" testId="refund-processing">
          <p>Approved refunds are processed to the original payment method through Stripe. Depending on your bank, the funds typically appear within five (5) to ten (10) business days.</p>
        </LegalSection>

        <LegalSection title="6. Chargebacks" id="chargebacks" testId="refund-chargebacks">
          <p>Please contact us before initiating a chargeback. Most refund questions are resolved faster and more transparently through a direct email than through a bank dispute.</p>
        </LegalSection>

        <LegalSection title="7. Exceptions" id="exceptions" testId="refund-exceptions">
          <p>
            We reserve the right to refuse a refund where we have evidence of fraud, plagiarism, or attempts to
            resell or redistribute our verified requirement graph.
          </p>
        </LegalSection>

        <LegalSection title="8. Contact" id="contact" testId="refund-contact">
          <p>
            {/* TODO: confirm exact legal entity name and mailing address before publishing */}
            Beyond Valencia LLC · Orlando, Florida ·{" "}
            <a href="mailto:hello@beyondvalencia.com" className="text-ivy hover:text-brass underline">
              hello@beyondvalencia.com
            </a>
          </p>
        </LegalSection>

        <p className="mt-16 text-xs text-stone italic" data-testid="refund-draft-disclaimer">
          This document is a draft pending legal review. It is not a substitute for advice from qualified counsel.
        </p>
      </div>
    </Layout>
  );
}
